/* eslint-disable i18next/no-literal-string -- TanStack cache key segments */
/**
 * `entities/project-comment` TanStack Query key factory.
 *
 * Keys are TARGET-scoped: `(projectId, targetType, targetId, versionId?)`.
 * A target is `{ type: 'file' | 'brief'; id }` — file comments pass
 * `targetType: 'file'` with the fileId as `targetId`; brief comments pass
 * `targetType: 'brief'` with the briefId. The replies sub-key extends the
 * comments tuple with `'replies'` + parentId.
 */

/** The kinds of entity a comment can be attached to. */
export type CommentTargetType = 'file' | 'brief';

export const PROJECT_COMMENT_QUERY_KEYS = {
  /**
   * Top-level comments for a target. When `versionId` is provided (file
   * comments) the key extends with `'version'` + the id so version-scoped
   * caches stay isolated from the all-versions cache.
   *
   * Sort-agnostic prefix — used by mutations to invalidate every sorted variant
   * at once via TanStack's prefix matching.
   */
  list: (projectId: string, targetType: CommentTargetType, targetId: string, versionId?: string) =>
    versionId
      ? (['project', projectId, 'comments', targetType, targetId, 'version', versionId] as const)
      : (['project', projectId, 'comments', targetType, targetId] as const),

  /**
   * Sort + visibility + version-scoped key for the infinite-query subscriber.
   * Extends the sort-agnostic `list` prefix (which may itself be version-scoped)
   * so root-list invalidations still hit every variant.
   */
  listSorted: (
    projectId: string,
    targetType: CommentTargetType,
    targetId: string,
    versionId: string | undefined,
    sortOrder: 'asc' | 'desc',
    showResolved: boolean,
  ) =>
    [
      ...PROJECT_COMMENT_QUERY_KEYS.list(projectId, targetType, targetId, versionId),
      { sortOrder, showResolved },
    ] as const,

  /** Replies to a specific parent comment. */
  replies: (projectId: string, targetType: CommentTargetType, targetId: string, parentId: string) =>
    ['project', projectId, 'comments', targetType, targetId, 'replies', parentId] as const,

  /**
   * Every active annotation (highlight anchor) for a target, independent of the
   * paginated comment list. Lives under the same `comments` prefix so it can be
   * invalidated alongside list mutations, but the `'annotations'` tag keeps it
   * isolated from the root-list and replies caches.
   */
  annotations: (projectId: string, targetType: CommentTargetType, targetId: string) =>
    ['project', projectId, 'comments', targetType, targetId, 'annotations'] as const,
} as const;

/**
 * Matches every root-list cache — unscoped (`[...comments, { sortOrder, showResolved }]`),
 * version-scoped (`[...comments, 'version', versionId, { sortOrder, showResolved }]`),
 * and the bare prefix forms used by mutations — but NOT the replies sub-cache
 * (`[...comments, 'replies', parentId]`). Use with `queryClient.setQueriesData`
 * so optimistic updates apply to whichever list cache the active view holds
 * without mutating unrelated replies caches.
 *
 * Tuple layout: `['project', projectId, 'comments', targetType, targetId, ...]`
 * — so `queryKey[2] === 'comments'` identifies a comments key, and the
 * `'replies'` / `'annotations'` tags (when present) land at index 5.
 */
export const isCommentsListKey = (queryKey: readonly unknown[]): boolean =>
  queryKey[2] === 'comments' && queryKey[5] !== 'replies' && queryKey[5] !== 'annotations';
